import {
  type AsyncResult,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import {
  type CacheDatum,
  CacheDatumState,
  type LoadedCacheDatum,
  type PermanentlyFailedCacheDatum,
} from "@/cache/CacheDatumState";
import { advancedThresholdValuePropertyName } from "@/cache/CustomGeoJSONCache/utils";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { FailureState, type RetryState, createRetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { emptyFeatureCollection } from "@/geojson";
import { generatePOILayerCacheId, generatePOILayerCacheIdAdvanced } from "@/layers/geojson/LayerUtils";
import {
  POI_BOUNDARY_STATE_PROPERTY,
  POI_INVALID_RESPONSE_VALUE,
  POI_INVALID_VALUE_STATE,
  POI_ORIGINAL_INDEX_PROPERTY,
  POI_OUT_OF_BOUNDS_STATE,
  POI_WITHIN_BOUNDS_STATE,
  partitionPoiCoordsByBoundary,
} from "@/layers/geojson/poi/poiBoundary";
import { recordLayerFetchMs } from "@/otel/metrics";
import type { AdvancedThreshold } from "@/overlay/components/CommonParts/Threshold/types";
import type { IconData } from "@/reducer/client-models";
import { dataProcessingThreadPool } from "@/threads/DataProcessingThread/DataProcessingThreadPool";
import { MiB } from "@/utility/bytesize";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  CoordinateSystem,
  type GeoJSONFeatureCollection,
  type JSONResponseDataPoints,
  type PointRequest,
  SinglePointCoordinate,
  WORLD_BBOX,
} from "@mm/api.meteomatics.com";
import type { MeteomaticsApiError } from "@mm/api.meteomatics.com";
import { type Abort, isAbortable } from "@mm/api.meteomatics.com";
import { isEmpty } from "lodash";

export interface PoiCacheStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
  featureCount: number;
  featurePending: number;
  featureEstimatedMemoryConsumption: number;
  dataCount: number;
  dataPending: number;
  dataEstimatedMemoryConsumption: number;
}

export enum PoiParameterRequestKind {
  SingleParameterColoring = "single parameter coloring",
}

const DEFAULT_POI_FEATURE_CACHE_MAX_SIZE = MiB(48);
const DEFAULT_POI_FEATURE_CACHE_DELAY_EVICTION_SIZE = MiB(64);
const DEFAULT_POI_DATA_CACHE_MAX_SIZE = MiB(64);
const DEFAULT_POI_DATA_CACHE_DELAY_EVICTION_SIZE = MiB(96);

/**
 * Advanced-threshold hydration is stored per generated property name and keyed by the original PoI index so
 * filtered API requests can still be merged back onto the full displayed point list.
 */
type PoiHydrationByOriginalIndex = Record<string, Record<number, number>>;

export class PoiCache implements Stats<PoiCacheStats> {
  // Cache for storing raw data from API response used for constructing POI GeoJson
  protected dataCache: { [key: string]: CacheDatum<JSONResponseDataPoints[], RetryState> } = {};
  protected cache: { [key: string]: CacheDatum<GeoJSONFeatureCollection, RetryState> } = {};

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_POI_FEATURE_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_POI_FEATURE_CACHE_DELAY_EVICTION_SIZE,
    private readonly maxDataSize: number = DEFAULT_POI_DATA_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilDataSize: number = DEFAULT_POI_DATA_CACHE_DELAY_EVICTION_SIZE,
  ) {}

  /**
   * Rebuilds the full rendered PoI item from the filtered API response.
   * In-bounds values are merged back by original point index, out-of-bounds points stay value-less, and
   * invalid `-999` responses are sanitized so they render as greyed-out markers without value pills.
   */
  private buildPreparedPoiItem(
    item: IconData,
    inBoundsIndexes: number[],
    coordinates: JSONResponseDataPoints[] = [],
    hydration: PoiHydrationByOriginalIndex = {},
  ): IconData {
    const dataByOriginalIndex = new Map<number, JSONResponseDataPoints>();
    const inBoundsIndexSet = new Set(inBoundsIndexes);

    inBoundsIndexes.forEach((originalIndex, requestIndex) => {
      const coordinate = coordinates[requestIndex];
      if (coordinate) {
        dataByOriginalIndex.set(originalIndex, coordinate);
      }
    });

    return {
      ...item,
      coords: item.coords.map((coord, originalIndex) => {
        const coordinate = dataByOriginalIndex.get(originalIndex);
        const isInvalidValue = coordinate?.dates?.[0]?.value === POI_INVALID_RESPONSE_VALUE;
        const hydrationEntries = Object.entries(hydration).reduce(
          (acc, [propertyName, valuesByIndex]) => {
            const value = valuesByIndex[originalIndex];
            if (value !== undefined) {
              acc[propertyName] = value;
            }
            return acc;
          },
          {} as Record<string, number>,
        );

        return {
          ...coord,
          ...(coordinate
            ? {
                ...coordinate,
                ...(isInvalidValue ? { dates: [] } : {}),
              }
            : {}),
          ...hydrationEntries,
          [POI_ORIGINAL_INDEX_PROPERTY]: originalIndex,
          [POI_BOUNDARY_STATE_PROPERTY]: !inBoundsIndexSet.has(originalIndex)
            ? POI_OUT_OF_BOUNDS_STATE
            : isInvalidValue
              ? POI_INVALID_VALUE_STATE
              : POI_WITHIN_BOUNDS_STATE,
        };
      }),
    };
  }

  stats(): PoiCacheStats {
    let featureCount = 0;
    let featurePending = 0;
    let dataCount = 0;
    let dataPending = 0;
    const featureEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.cache);
    const dataEstimatedMemoryConsumption = sumLoadedCacheMemoryConsumption(this.dataCache);

    for (const line in this.cache) {
      if (Object.hasOwn(this.cache, line)) {
        featureCount++;

        if (this.cache[line].state === CacheDatumState.Pending) {
          featurePending++;
        }
      }
    }

    for (const line in this.dataCache) {
      if (Object.hasOwn(this.dataCache, line)) {
        dataCount++;

        if (this.dataCache[line].state === CacheDatumState.Pending) {
          dataPending++;
        }
      }
    }

    return {
      count: featureCount + dataCount,
      pending: featurePending + dataPending,
      estimatedMemoryConsumption: featureEstimatedMemoryConsumption + dataEstimatedMemoryConsumption,
      featureCount,
      featurePending,
      featureEstimatedMemoryConsumption,
      dataCount,
      dataPending,
      dataEstimatedMemoryConsumption,
    };
  }

  descJsx(): JSX.Element {
    return (
      <>
        Special-purpose cache for POI requests. Replacement policy is approximate memory-based LRU with separate
        rendered and raw-data pools.
      </>
    );
  }

  clear() {
    this.cache = {};
    this.dataCache = {};
  }

  label(): string {
    return this.label_;
  }

  private markFeatureEntryUsed(cacheLine: LoadedCacheDatum<GeoJSONFeatureCollection>) {
    markCacheDatumUsed(cacheLine);
  }

  private markDataEntryUsed(cacheLine: LoadedCacheDatum<JSONResponseDataPoints[]>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadFeatureEntry(_: CacheDatum<GeoJSONFeatureCollection, RetryState> | undefined, cacheId: string) {
    delete this.cache[cacheId];
  }

  private unloadDataEntry(_: CacheDatum<JSONResponseDataPoints[], RetryState> | undefined, cacheId: string) {
    delete this.dataCache[cacheId];
  }

  private pruneFeatureCache() {
    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadFeatureEntry.bind(this));
  }

  private pruneDataCache() {
    evictCacheEntriesBySize(
      this.dataCache,
      this.maxDataSize,
      this.delayEvictionUntilDataSize,
      this.unloadDataEntry.bind(this),
    );
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  /**
   * A method to return a cached data from cache.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @param request
   * @param kind
   * @returns
   */
  peekCache(cacheId: string): AsyncResult<GeoJSONFeatureCollection> {
    const v = this.cache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult(emptyFeatureCollection);
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
        // mark it as such to prevent concurrent invocations from reentering the remaining function body
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult(emptyFeatureCollection);
      case CacheDatumState.Loaded:
        this.markFeatureEntryUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  /**
   * A method to return a cached data from cache containing raw parameter values.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @returns
   */
  peekDataCache(cacheId: string): AsyncResult<JSONResponseDataPoints[]> {
    const v = this.dataCache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult([]);
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        const retry_ = v.payload;
        if (retry_.failureState !== FailureState.FailedTemporarily) {
          return createPendingAsyncResult(v.promise);
        }
        // since the current invocation moves the state from [Pending/FailedTemporarily] to [Pending/Queued]
        // mark it as such to prevent concurrent invocations from reentering the remaining function body
        retry_.failureState = undefined;
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult([]);
      case CacheDatumState.Loaded:
        this.markDataEntryUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  /**
   * Returns cached or freshly fetched raw point values for the filtered request.
   * Empty filtered requests resolve immediately to `[]` so callers can reuse the same data path even when
   * every configured PoI lies outside the current model boundary.
   */
  retrieveParameter(
    request: PointRequest<CoordinateSystem.WGS84>,
    kind: PoiParameterRequestKind = PoiParameterRequestKind.SingleParameterColoring,
  ): AsyncResult<JSONResponseDataPoints[]> {
    const cacheId = generatePOILayerCacheIdAdvanced(request, kind);
    if (Object.hasOwn(this.dataCache, cacheId)) {
      return this.peekDataCache(cacheId);
    }

    if (request.coordinates.length === 0) {
      const cacheData: LoadedCacheDatum<JSONResponseDataPoints[]> = createLoadedCacheDatum([], performance.now());
      this.markDataEntryUsed(cacheData);
      this.dataCache[cacheId] = cacheData;
      this.pruneDataCache();
      return createResolvedAsyncResult([]);
    }

    const retry_: RetryState | null = null;
    const modelBoundingBox = request.boundingBoxLimit || WORLD_BBOX;
    const retry = retry_ ?? createRetryState(modelBoundingBox);
    const fetchStarted = performance.now();
    const promise = apiThreadPool
      .GetPointJSON(request, retry)
      .then((result) => {
        recordLayerFetchMs({
          durationMs: performance.now() - fetchStarted,
          source: "poi",
        });
        if (isEmpty(result.data)) {
          return [];
        }
        const { coordinates } = result.data[0];
        const pendingEntry = this.dataCache[cacheId];
        const requestDatetime = pendingEntry?.requestDatetime ?? performance.now();
        const cacheData: LoadedCacheDatum<JSONResponseDataPoints[]> = createLoadedCacheDatum(
          coordinates,
          requestDatetime,
        );
        this.markDataEntryUsed(cacheData);
        this.dataCache[cacheId] = cacheData;
        this.pruneDataCache();
        return coordinates;
      })
      .catch((e: MeteomaticsApiError | Abort) => {
        if (isAbortable(e)) {
          delete this.dataCache[cacheId];
          return Promise.reject(e);
        }
        return this.reactOnMeteomaticsApiError(e, retry, cacheId, this.dataCache);
      });

    this.dataCache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise,
      payload: retry,
    };
    return createPendingAsyncResult(promise);
  }

  /**
   * Returns the rendered GeoJSON for a PoI layer.
   * The feature cache key still represents the full displayed point set, while the underlying raw-data request
   * may be filtered down to the subset of coordinates that are valid for the selected model boundary.
   */
  retrievePoiByParameter(
    request: PointRequest<CoordinateSystem.WGS84>,
    item: IconData,
    originalIndexes: number[],
    kind: PoiParameterRequestKind = PoiParameterRequestKind.SingleParameterColoring,
    refresh?: boolean,
    advancedThresholds?: AdvancedThreshold[],
  ): AsyncResult<GeoJSONFeatureCollection> {
    const advancedThresholdSignature = JSON.stringify(advancedThresholds ?? []);
    const cacheId = generatePOILayerCacheId(request, kind, item, advancedThresholdSignature);
    if (!refresh && Object.hasOwn(this.cache, cacheId)) {
      return this.peekCache(cacheId);
    }

    // if there is no parameter or no items we don't need to request anything.
    if (request.parameters[0] === "" || item.coords.length === 0) {
      const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(
        emptyFeatureCollection,
        performance.now(),
      );
      this.markFeatureEntryUsed(cacheData);
      this.cache[cacheId] = cacheData;
      this.pruneFeatureCache();
      return createResolvedAsyncResult(emptyFeatureCollection);
    }

    const retry_: RetryState | null = null;
    const modelBoundingBox = request.boundingBoxLimit || WORLD_BBOX;
    const retry = retry_ ?? createRetryState(modelBoundingBox);

    // Run hydration in parallel if provided
    const hydrationPromise: Promise<PoiHydrationByOriginalIndex> =
      advancedThresholds && advancedThresholds.length > 0
        ? this.hydratePointFeaturesAdvancedThreshold(request, item, advancedThresholds)
        : Promise.resolve({});

    const { asynchronous } = this.retrieveParameter(request, kind);

    const promise = Promise.all([asynchronous, hydrationPromise])
      .then(([res, hydration]) => {
        const preparedItem = this.buildPreparedPoiItem(item, originalIndexes, res, hydration);
        return dataProcessingThreadPool.createPOIGeoJson(preparedItem);
      })
      .then((result) => {
        const pendingEntry = this.cache[cacheId];
        const requestDatetime = pendingEntry?.requestDatetime ?? performance.now();
        // This cache is created WITH icon stuff AND response data AND hydration for advanced threshold
        const cacheData: LoadedCacheDatum<GeoJSONFeatureCollection> = createLoadedCacheDatum(result, requestDatetime);
        this.markFeatureEntryUsed(cacheData);
        this.cache[cacheId] = cacheData;
        this.pruneFeatureCache();
        return result;
      })
      .catch((e: MeteomaticsApiError | Abort) => {
        if (isAbortable(e)) {
          delete this.cache[cacheId];
          return Promise.reject(e);
        }
        return this.reactOnMeteomaticsApiError(e, retry, cacheId, this.cache);
      });

    this.cache[cacheId] = {
      requestDatetime: performance.now(),
      state: CacheDatumState.Pending,
      promise,
      payload: retry,
    };
    return createPendingAsyncResult(promise);
  }

  /**
   * Fetches advanced-threshold parameters with each detail's own model boundary and maps those values back to
   * the original PoI indexes so threshold coloring stays aligned with the unfiltered rendered feature order.
   */
  hydratePointFeaturesAdvancedThreshold(
    baseRequest: PointRequest<CoordinateSystem.WGS84>,
    item: IconData,
    advancedThresholds: AdvancedThreshold[],
  ): Promise<PoiHydrationByOriginalIndex> {
    const thresholdDetails = advancedThresholds
      .flatMap((t) => t.details ?? [])
      .filter((detail) => detail.parameter && detail.model);
    const promises = thresholdDetails.map((detail) => {
      const modelBoundingBox = getModelBoundingBox(detail.model);
      const partition = partitionPoiCoordsByBoundary(item.coords, modelBoundingBox);
      const resultKey = advancedThresholdValuePropertyName(detail.id);
      const request = {
        ...baseRequest,
        coordinates: partition.inBoundsCoords.map(
          (coord) => new SinglePointCoordinate<CoordinateSystem.WGS84>(coord.lat, coord.lon, CoordinateSystem.WGS84),
        ),
        model: detail.model,
        parameters: [detail.parameter] as [string, ...string[]],
        startDatetime: baseRequest.startDatetime.plus({ minutes: detail.offsetMinutes }),
        requestOptions: {
          calibrated: !!detail.calibrated,
        },
        apiUrlSuffix: detail.apiUrlSuffix,
        boundingBoxLimit: modelBoundingBox,
      };
      return this.retrieveParameter(request).asynchronous.then((coords) => {
        const valuesByOriginalIndex = partition.inBoundsIndexes.reduce(
          (acc, originalIndex, requestIndex) => {
            const value = coords[requestIndex]?.dates?.[0]?.value;
            if (value !== undefined && value !== POI_INVALID_RESPONSE_VALUE) {
              acc[originalIndex] = value;
            }
            return acc;
          },
          {} as Record<number, number>,
        );

        return { [resultKey]: valuesByOriginalIndex };
      });
    });

    return Promise.allSettled(promises).then((results) => {
      const combinedValues = results.reduce((acc, result) => {
        if (result.status === "fulfilled") {
          Object.assign(acc, result.value);
        }
        return acc;
      }, {} as PoiHydrationByOriginalIndex);
      return combinedValues;
    });
  }

  reactOnMeteomaticsApiError<T>(
    e: MeteomaticsApiError,
    retry: RetryState,
    cacheId: string,
    cache: Record<string, CacheDatum<T, RetryState>>,
  ): Promise<never> {
    // Todo: Make different reactions on Errors, currently always return a CacheDatumState.PermanentlyFailed
    // ToDo: Handle Out of bounds.
    switch (e.kind) {
      case "BoundsError":
      case "TimeError":
      case "ComputationTimeoutError":
      case "AuthError":
      case "UnknownError":
      case "UnknownParameterError":
      case "MissingDataError": {
        const { requestDatetime } = cache[cacheId];
        const cacheData: PermanentlyFailedCacheDatum<null> = {
          requestDatetime,
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };
        cache[cacheId] = cacheData as CacheDatum<T, RetryState>;
        // retry.failureState = FailureState.FailedPermanently;
        return Promise.reject(e);
      }
      default: {
        const _exhaustive: never = e;
        return _exhaustive;
      }
    }
    // Still here for retry functionality
    // Retry would be something for boundsError -> cropping bbox and retry ...
    // retry.failedAttempts++; // accumulated failures for the current tile only
    // retry.failureState = FailureState.FailedTemporarily;
    // if (retry.failedAttempts > NETWORK_EXPONENTIAL_BACKOFF_MAX_RETRIES) {
    //   retry.failureState = FailureState.FailedPermanently;
    //   return Promise.reject(e);
    // }
    //
    // return Promise.reject(e);
  }
}
